import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { erpIntegrationService } from '../../../services/erpIntegrationService';

const ERPConnectorMatrix = ({ connections, onConnectionCreated, onConnectionUpdated }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    erpSystem: '',
    connectionName: '',
    apiEndpoint: '',
    apiKey: '',
    syncFrequency: 'hourly',
    syncDirection: 'bidirectional'
  });
  const [loading, setLoading] = useState(false);

  const erpSystems = [
    { value: 'pronoto_erp', label: 'Pronoto ERP', description: 'PNG-based comprehensive ERP solution' },
    { value: 'sap_business_one', label: 'SAP Business One', description: 'Enterprise resource planning' },
    { value: 'myob', label: 'MYOB AccountRight', description: 'Accounting and payroll software' },
    { value: 'quickbooks_enterprise', label: 'QuickBooks Enterprise', description: 'Advanced business management' },
    { value: 'sage_300', label: 'Sage 300', description: 'Cloud-based ERP system' },
    { value: 'netsuite', label: 'NetSuite', description: 'Cloud ERP and financials' }
  ];

  const handleCreateConnection = async (e) => {
    e?.preventDefault();
    try {
      setLoading(true);
      await erpIntegrationService?.createConnection({
        erpSystem: formData?.erpSystem,
        connectionName: formData?.connectionName,
        apiEndpoint: formData?.apiEndpoint,
        apiKeyEncrypted: formData?.apiKey,
        syncFrequency: formData?.syncFrequency,
        syncDirection: formData?.syncDirection,
        createdBy: 'current_user@example.com'
      });
      setShowAddModal(false);
      setFormData({
        erpSystem: '',
        connectionName: '',
        apiEndpoint: '',
        apiKey: '',
        syncFrequency: 'hourly',
        syncDirection: 'bidirectional'
      });
      onConnectionCreated?.();
    } catch (error) {
      console.error('Error creating connection:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      connected: { label: 'Connected', color: 'bg-success/10 text-success', icon: 'CheckCircle' },
      disconnected: { label: 'Disconnected', color: 'bg-error/10 text-error', icon: 'XCircle' },
      testing: { label: 'Testing', color: 'bg-warning/10 text-warning', icon: 'Loader' },
      error: { label: 'Error', color: 'bg-error/10 text-error', icon: 'AlertCircle' },
      pending_setup: { label: 'Pending Setup', color: 'bg-muted text-muted-foreground', icon: 'Clock' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending_setup;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">ERP System Connections</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure and manage connections to PNG ERP systems
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Icon name="Plus" size={16} />
          Add Connection
        </Button>
      </div>

      {/* Connections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {connections?.map((connection) => (
          <div
            key={connection?.id}
            className="bg-background border border-border rounded-lg p-5 hover:border-primary/50 transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">
                  {erpIntegrationService?.getERPSystemLabel(connection?.erpSystem)}
                </h3>
                <p className="text-sm text-muted-foreground">{connection?.connectionName}</p>
              </div>
              {getStatusBadge(connection?.connectionStatus)}
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Link" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Sync:</span>
                <span className="text-foreground font-medium capitalize">
                  {connection?.syncDirection?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Frequency:</span>
                <span className="text-foreground font-medium capitalize">
                  {connection?.syncFrequency}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Activity" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Health:</span>
                <span className={`font-medium capitalize ${
                  connection?.healthStatus === 'healthy' ? 'text-success' :
                  connection?.healthStatus === 'degraded' ? 'text-warning' :
                  connection?.healthStatus === 'critical'? 'text-error' : 'text-muted-foreground'
                }`}>
                  {connection?.healthStatus}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button className="flex-1 px-3 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors text-sm font-medium">
                Configure
              </button>
              <button className="px-3 py-2 border border-border rounded hover:bg-muted transition-colors">
                <Icon name="Settings" size={16} className="text-muted-foreground" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Connection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">Add ERP Connection</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateConnection} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  ERP System *
                </label>
                <Select
                  value={formData?.erpSystem}
                  onChange={(e) => setFormData({ ...formData, erpSystem: e?.target?.value })}
                  required
                >
                  <option value="">Select ERP System</option>
                  {erpSystems?.map((system) => (
                    <option key={system?.value} value={system?.value}>
                      {system?.label} - {system?.description}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Connection Name *
                </label>
                <Input
                  type="text"
                  value={formData?.connectionName}
                  onChange={(e) => setFormData({ ...formData, connectionName: e?.target?.value })}
                  placeholder="e.g., Pronoto ERP - Main Connection"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  API Endpoint *
                </label>
                <Input
                  type="url"
                  value={formData?.apiEndpoint}
                  onChange={(e) => setFormData({ ...formData, apiEndpoint: e?.target?.value })}
                  placeholder="https://api.erpsystem.com/v1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  API Key *
                </label>
                <Input
                  type="password"
                  value={formData?.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e?.target?.value })}
                  placeholder="Enter API key"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Sync Direction
                  </label>
                  <Select
                    value={formData?.syncDirection}
                    onChange={(e) => setFormData({ ...formData, syncDirection: e?.target?.value })}
                  >
                    <option value="bidirectional">Bidirectional</option>
                    <option value="hr_to_erp">HR to ERP</option>
                    <option value="erp_to_hr">ERP to HR</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Sync Frequency
                  </label>
                  <Select
                    value={formData?.syncFrequency}
                    onChange={(e) => setFormData({ ...formData, syncFrequency: e?.target?.value })}
                  >
                    <option value="realtime">Real-time</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="custom">Custom</option>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Connection'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="bg-muted text-foreground hover:bg-muted/80"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPConnectorMatrix;
